<template>
  <div class="video-campanha">
    <iframe
      ref="iframe"
      sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
      :src="src"
      class="video-campanha__iframe"
      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
      allowfullscreen
      referrerpolicy="strict-origin-when-cross-origin"
      title="Vídeo da campanha"
    ></iframe>
  </div>
</template>

<script>
import { criarPlayerGumlet } from "@/utils/playerGumlet";

// Se a pessoa não der play neste tempo, o vídeo começa sozinho.
export const AUTOPLAY_APOS_MS = 10000;

// Os botões do modal ficam presos até o vídeo terminar, e o modal não fecha
// sem resposta. Se o player nunca ficar pronto (Gumlet fora, rede da lotérica
// barrando, vídeo apagado) a pessoa ficaria trancada — então, passado este
// tempo, os botões liberam. É a única saída sem assistir.
export const TEMPO_LIMITE_PRONTO_MS = 20000;

// Navegador costuma barrar play automático COM som. Se o play pedido não
// virar evento `play` neste tempo, tenta de novo sem som.
export const ESPERA_SOM_BLOQUEADO_MS = 1500;

// Trava de avanço: entre dois `timeupdate`, a posição só pode passar do que já
// foi assistido o que o relógio permite — tempo real tocando vezes a maior
// velocidade do player, mais uma folga para o intervalo irregular do evento.
// Comparar só posições deixava atravessar o vídeo em pulos pequenos seguidos.
export const VELOCIDADE_MAXIMA = 2;
export const FOLGA_AVANCO_S = 0.75;

// Pausado não há crédito de tempo — senão pausar, esperar e arrastar a barra
// passaria. O teto cobre só o caso de o evento `play` chegar depois dos
// primeiros `timeupdate`.
export const CREDITO_PARADO_MAX_S = 1;

// O `ended` só vale se o assistido chegou até aqui do fim.
export const TOLERANCIA_FIM_S = 3;

export default {
  name: "video-campanha",
  emits: ["liberar"],
  props: {
    src: { type: String, required: true },
  },
  data() {
    return {
      pronto: false,
      tocou: false,
      maxAssistido: 0,
      tocando: false,
      ultimoRelogio: null,
      duracao: 0,
      liberado: false,
    };
  },
  mounted() {
    // Fora do `data` de propósito: não é estado de tela e não deve ser reativo.
    this.timers = [];
    this.player = criarPlayerGumlet(this.$refs.iframe, {
      ready: this.aoFicarPronto,
      play: this.aoTocar,
      pause: this.aoPausar,
      ended: this.aoTerminar,
      timeupdate: this.aoAndar,
      error: () => this.liberar("indisponivel"),
    });
    this.agendar(() => {
      if (!this.pronto) {
        this.liberar("indisponivel");
      }
    }, TEMPO_LIMITE_PRONTO_MS);
  },
  beforeUnmount() {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers = [];
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  },
  methods: {
    agendar(fn, ms) {
      this.timers.push(setTimeout(fn, ms));
    },
    aoFicarPronto() {
      this.pronto = true;
      this.agendar(this.tocarSozinho, AUTOPLAY_APOS_MS);
    },
    aoTocar() {
      this.tocou = true;
      this.tocando = true;
      // O tempo pausado antes do play não conta como assistido.
      this.ultimoRelogio = Date.now();
    },
    aoPausar() {
      this.tocando = false;
    },
    // Quanto a posição pode passar do já assistido neste `timeupdate`.
    creditoDeAvanco() {
      const agora = Date.now();
      const decorrido = this.ultimoRelogio === null ? 0 : (agora - this.ultimoRelogio) / 1000;
      this.ultimoRelogio = agora;
      const tempoValido = this.tocando ? decorrido : Math.min(decorrido, CREDITO_PARADO_MAX_S);
      return tempoValido * VELOCIDADE_MAXIMA + FOLGA_AVANCO_S;
    },
    tocarSozinho() {
      if (this.tocou || !this.player) {
        return;
      }
      this.player.play();
      this.agendar(() => {
        if (!this.tocou && this.player) {
          this.player.mute();
          this.player.play();
        }
      }, ESPERA_SOM_BLOQUEADO_MS);
    },
    aoAndar({ seconds, duration }) {
      if (!this.player) {
        return;
      }
      const posicao = Number(seconds) || 0;
      if (Number(duration) > 0) {
        this.duracao = Number(duration);
      }
      // Voltar é livre; avançar, só o que o relógio permite.
      if (posicao > this.maxAssistido + this.creditoDeAvanco()) {
        this.player.setCurrentTime(this.maxAssistido);
        return;
      }
      this.maxAssistido = Math.max(this.maxAssistido, posicao);
    },
    aoTerminar() {
      this.tocando = false;
      // Um `ended` que chega logo depois de um pulo para o final não conta:
      // o que vale é ter assistido até perto do fim. Sem duração conhecida
      // não há como medir, e prender a pessoa seria pior.
      const assistiuAteOFim = this.duracao === 0 || this.maxAssistido >= this.duracao - TOLERANCIA_FIM_S;
      if (assistiuAteOFim) {
        this.liberar("fim");
      }
    },
    liberar(motivo) {
      if (this.liberado) {
        return;
      }
      this.liberado = true;
      this.$emit("liberar", motivo);
    },
  },
};
</script>

<style lang="stylus" scoped>
.video-campanha
  position relative
  width 100%
  aspect-ratio 16 / 9
  background #000
  border-radius 8px
  overflow hidden

.video-campanha__iframe
  position absolute
  top 0
  left 0
  width 100%
  height 100%
  border 0
</style>
